import React, {FC, ReactNode} from "react";
import {Link, useLocation} from "react-router-dom";
import {Spin} from "antd";
import {HomeFilled, HomeOutlined, CalendarFilled, CalendarOutlined, UserOutlined} from "@ant-design/icons";
import {useGetCompanyInfoQuery} from "../../generated/graphql";
import {URL_ASSISTANT_HOME, URL_ASSISTANT_MY, URL_ASSISTANT_PROFILE} from "../../models/url";
import {useAssistantTheme} from "./AssistantThemeContext";
import {useLastUpdated} from "./LastUpdatedContext";
import {useLogoAnalysis} from "./useLogoAnalysis";
import KoLogo from "../../assets/logo.svg";
import styles from "./assistantApp.less";

type AssistantLayoutProps = {
    children: ReactNode;
};

type TabDef = {
    path: string;
    label: string;
    iconActive: ReactNode;
    iconInactive: ReactNode;
};

const tabs: TabDef[] = [
    {
        path: URL_ASSISTANT_HOME,
        label: "Dienste",
        iconActive: <HomeFilled />,
        iconInactive: <HomeOutlined />,
    },
    {
        path: URL_ASSISTANT_MY,
        label: "Meine",
        iconActive: <CalendarFilled />,
        iconInactive: <CalendarOutlined />,
    },
    {
        path: URL_ASSISTANT_PROFILE,
        label: "Profil",
        iconActive: <UserOutlined style={{fontWeight: 700}} />,
        iconInactive: <UserOutlined />,
    },
];

const formatTime = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return hours + ":" + minutes;
};

const AssistantLayout: FC<AssistantLayoutProps> = ({children}) => {
    const location = useLocation();
    const {resolvedTheme} = useAssistantTheme();
    const {lastUpdated} = useLastUpdated();
    const {data: companyInfoData, loading: companyInfoLoading} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});

    const logo = companyInfoData?.companyInfo?.companySettings?.logo;
    const companyName = companyInfoData?.companyInfo?.name;
    const {needsBackground} = useLogoAnalysis(logo, resolvedTheme);

    const themeClass = resolvedTheme === "dark" ? styles["assistant-theme-dark"] : styles["assistant-theme-light"];

    if (companyInfoLoading && !companyInfoData) {
        return (
            <div className={`${themeClass} ${styles.assistantLoading}`}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className={`${themeClass} ${styles.assistantRoot}`}>
            {/* Header */}
            <div className={styles.assistantHeader}>
                <div className={styles.assistantHeaderLeft}>
                    <img
                        src={KoLogo}
                        alt="KursOrganizer"
                        className={styles.assistantHeaderLogo}
                    />
                    <div className={styles.assistantHeaderInfo}>
                        <span className={styles.assistantHeaderTitle}>AssistenzApp</span>
                        {lastUpdated ? (
                            <span className={styles.assistantHeaderUpdated}>
                                {"Aktualisiert " + formatTime(lastUpdated)}
                            </span>
                        ) : null}
                    </div>
                </div>
                {logo ? (
                    <div className={needsBackground ? styles.assistantHeaderTenantLogo : styles.assistantHeaderTenantLogoTransparent}>
                        <img
                            src={`data:image/png;base64,${logo}`}
                            alt={companyName || "Logo"}
                        />
                    </div>
                ) : null}
            </div>

            {/* Content */}
            <div className={styles.assistantContent}>
                {children}
            </div>

            {/* Tab Bar */}
            <div className={styles.assistantTabBar}>
                {tabs.map((tab) => {
                    const isActive = location.pathname === tab.path;
                    return (
                        <Link
                            key={tab.path}
                            to={tab.path}
                            className={`${styles.assistantTab} ${isActive ? styles.assistantTabActive : ""}`}
                        >
                            <span className={styles.assistantTabIcon}>
                                {isActive ? tab.iconActive : tab.iconInactive}
                            </span>
                            <span className={styles.assistantTabLabel}>{tab.label}</span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default AssistantLayout;
